/**
 * Formulaire d'une charge fixe — création et modification.
 *
 * Même mécanique que le formulaire BtoB : l'état repart de `editing` quand on
 * change de charge à modifier, et une création vide le libellé, le montant et
 * la note mais garde catégorie, fréquence et dates (on saisit souvent
 * plusieurs salaires d'affilée).
 */

import {
  CHARGE_AMOUNT_MAX,
  CHARGE_CATEGORIES,
  CHARGE_CATEGORY_LABELS,
  CHARGE_FREQUENCIES,
  CHARGE_FREQUENCY_LABELS,
  CHARGE_LABEL_MAX_LENGTH,
  CHARGE_NOTE_MAX_LENGTH,
  type ChargeCategory,
  type ChargeFrequency,
  type FixedCharge,
  type FixedChargeInput,
} from '@shared/types/charges';
import { useEffect, useState, type FormEvent } from 'react';

import { todayInParis } from '../lib/dates';
import { Card } from './Card';

interface Draft {
  label: string;
  category: ChargeCategory;
  amount: string;
  frequency: ChargeFrequency;
  startDate: string;
  endDate: string;
  note: string;
}

const firstOfMonth = () => `${todayInParis().slice(0, 7)}-01`;

const emptyDraft = (): Draft => ({
  label: '',
  category: 'salaries',
  amount: '',
  frequency: 'monthly',
  startDate: firstOfMonth(),
  endDate: '',
  note: '',
});

const toDraft = (charge: FixedCharge): Draft => ({
  label: charge.label,
  category: charge.category,
  amount: String(charge.amount).replace('.', ','),
  frequency: charge.frequency,
  startDate: charge.startDate,
  endDate: charge.endDate ?? '',
  note: charge.note ?? '',
});

/** « 1 250,50 » → 1250.5 ; `null` si illisible. */
const parseAmount = (raw: string): number | null => {
  const cleaned = raw.replace(/\s/g, '').replace('€', '').replace(',', '.');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};

export function ChargeForm({
  editing,
  isSaving,
  onSubmit,
  onCancel,
}: {
  editing: FixedCharge | null;
  isSaving: boolean;
  onSubmit: (input: FixedChargeInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(editing ? toDraft(editing) : emptyDraft());
    setError(null);
  }, [editing]);

  const set = <K extends keyof Draft>(key: K) => (event: { target: { value: string } }) =>
    setDraft((current) => ({ ...current, [key]: event.target.value as Draft[K] }));

  const once = draft.frequency === 'once';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const amount = parseAmount(draft.amount);
    if (amount === null || amount < 0 || amount > CHARGE_AMOUNT_MAX) return setError('Montant : un nombre positif.');
    if (!once && draft.endDate && draft.endDate < draft.startDate) return setError('La date de fin précède la date de début.');
    setError(null);
    try {
      await onSubmit({
        label: draft.label,
        category: draft.category,
        amount,
        frequency: draft.frequency,
        startDate: draft.startDate,
        endDate: once ? null : draft.endDate || null,
        note: draft.note || null,
      });
      if (!editing) setDraft((current) => ({ ...current, label: '', amount: '', note: '' }));
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Enregistrement impossible.');
    }
  };

  return (
    <Card
      title={editing ? 'Modifier la charge' : 'Ajouter une charge'}
      subtitle="Montant HT par échéance. Une charge récurrente est lissée jour par jour sur la période affichée ; une charge ponctuelle compte en entier à sa date."
      className="marker-form-card"
    >
      <form className="marker-form" onSubmit={(event) => void handleSubmit(event)}>
        <div className="field marker-form__grow">
          <label className="field__label" htmlFor="charge-label">Libellé</label>
          <input
            id="charge-label"
            type="text"
            required
            maxLength={CHARGE_LABEL_MAX_LENGTH}
            value={draft.label}
            onChange={set('label')}
            placeholder="ex. Salaire développeur, Loyer bureau"
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="charge-category">Catégorie</label>
          <select id="charge-category" value={draft.category} onChange={set('category')}>
            {CHARGE_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {CHARGE_CATEGORY_LABELS[category]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="charge-amount">Montant HT (€)</label>
          <input id="charge-amount" type="text" inputMode="decimal" required value={draft.amount} onChange={set('amount')} style={{ width: 110 }} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="charge-frequency">Fréquence</label>
          <select id="charge-frequency" value={draft.frequency} onChange={set('frequency')}>
            {CHARGE_FREQUENCIES.map((frequency) => (
              <option key={frequency} value={frequency}>
                {CHARGE_FREQUENCY_LABELS[frequency]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="charge-start">{once ? 'Date' : 'Début'}</label>
          <input id="charge-start" type="date" required value={draft.startDate} onChange={set('startDate')} />
        </div>
        {!once && (
          <div className="field">
            <label className="field__label" htmlFor="charge-end">Fin (facultative)</label>
            <input id="charge-end" type="date" value={draft.endDate} onChange={set('endDate')} />
          </div>
        )}
        <div className="field marker-form__grow">
          <label className="field__label" htmlFor="charge-note">Note</label>
          <input id="charge-note" type="text" maxLength={CHARGE_NOTE_MAX_LENGTH} value={draft.note} onChange={set('note')} />
        </div>
        <div className="marker-form__actions">
          <button type="submit" className="btn btn--primary" disabled={isSaving}>
            {isSaving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Ajouter'}
          </button>
          {editing && (
            <button type="button" className="btn" onClick={onCancel} disabled={isSaving}>
              Annuler
            </button>
          )}
        </div>
        {error && (
          <p className="login__error marker-form__error" role="alert">
            {error}
          </p>
        )}
      </form>
    </Card>
  );
}
